import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { SITE } from '../config/media';
import Brand from './Brand';
import UserChip from './UserChip';
import { useAuth } from '../contexts/AuthContext';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from './ui/dropdown-menu';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from './ui/accordion';

/* ------------------------------------------------------------------ *
 * Information architecture (single source of truth - drives both the
 * desktop dropdowns/mega-menu and the mobile accordion drawer).
 * ------------------------------------------------------------------ */

const PRODUCTS_LINKS = [
    { to: '/access-mats', label: 'Access Mats' },
    { to: '/rig-mats', label: 'Rig Mats' },
    { to: '/crane-mats', label: 'Crane Mats' },
];
const PRODUCTS_SECONDARY = [
    { to: '/catalog', label: 'All Products' },
    { to: '/#calculator', label: 'Field Calculator', isCalc: true },
];
const PRODUCTS_ALL_PATHS = [
    ...PRODUCTS_LINKS.map((l) => l.to),
    '/catalog',
];

const PROJECTS_LINKS = [
    { to: '/projects', label: 'All Projects' },
    { to: '/projects/infrastructure', label: 'Infrastructure' },
    { to: '/projects/oil-gas-pipeline', label: 'Oil & Gas / Pipeline' },
    { to: '/projects/renewables', label: 'Wind & Renewables' },
];

const PARTNERSHIPS_LINKS = [
    { to: '/partnerships/indigenous', label: 'Indigenous Partnerships' },
    { to: '/partnerships/procurement', label: 'Procurement Partnerships' },
    { to: '/partnerships/sustainability', label: 'Sustainability' },
];

/* ------------------------------------------------------------------ *
 * Utilities
 * ------------------------------------------------------------------ */

const isPathActive = (pathname, paths) =>
    paths.some((p) => pathname === p || pathname.startsWith(p + '/'));

/**
 * Click handler for the "Field Calculator" entry. Smooth-scrolls to the
 * homepage `#calculator` anchor (alias of #field-calculator). If the user
 * is on a different route, navigate to `/` first; the home page picks up
 * the hash via its useEffect and scrolls once mounted.
 */
const useScrollToCalculator = (closeMenu) => {
    const location = useLocation();
    const navigate = useNavigate();
    return (e) => {
        e.preventDefault();
        if (closeMenu) closeMenu();
        if (location.pathname === '/') {
            const el = document.getElementById('calculator');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            navigate('/#calculator');
        }
    };
};

/* ------------------------------------------------------------------ *
 * Top-level desktop nav primitives
 * ------------------------------------------------------------------ */

const TriggerCls = (active) =>
    [
        'group inline-flex items-center gap-1 whitespace-nowrap rounded-none border border-transparent px-1 py-2 font-sans text-sm font-medium uppercase tracking-[0.12em] outline-none transition-colors duration-150',
        active ? 'text-brand-gold' : 'text-brand-muted hover:text-brand-text',
        'focus-visible:text-brand-text data-[state=open]:text-brand-gold',
    ].join(' ');

const ItemLinkCls = (active) =>
    [
        'block w-full cursor-pointer px-4 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] outline-none transition-colors',
        active
            ? 'text-brand-gold'
            : 'text-brand-text hover:text-brand-gold focus-visible:text-brand-gold',
    ].join(' ');

const ProductsMenu = ({ pathname }) => {
    const active = isPathActive(pathname, PRODUCTS_ALL_PATHS);
    const onCalc = useScrollToCalculator();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                data-testid="nav-products-trigger"
                aria-haspopup="menu"
                className={TriggerCls(active)}
            >
                Products
                <ChevronDown
                    size={14}
                    className="transition-transform duration-150 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                sideOffset={14}
                data-testid="nav-products-content"
                className="w-[380px] max-w-[92vw] border border-brand-hairline bg-brand-surface p-0 text-brand-text shadow-[0_18px_44px_-16px_rgba(0,0,0,0.7)]"
            >
                <div className="p-6">
                    <p className="font-sans text-[13px] leading-relaxed text-brand-muted">
                        Matting solutions for Western Canada&rsquo;s most difficult terrain.
                    </p>
                    <ul className="mt-5 flex flex-col">
                        {PRODUCTS_LINKS.map((l) => (
                            <DropdownMenuItem key={l.to} asChild className="p-0 focus:bg-transparent">
                                <Link
                                    to={l.to}
                                    data-testid={`nav-products-${l.to.replace('/', '')}`}
                                    className={ItemLinkCls(pathname === l.to)}
                                >
                                    {l.label}
                                </Link>
                            </DropdownMenuItem>
                        ))}
                    </ul>
                    <div
                        aria-hidden="true"
                        className="my-3 h-px w-full bg-brand-blue-soft"
                    />
                    <ul className="flex flex-col">
                        {PRODUCTS_SECONDARY.map((l) =>
                            l.isCalc ? (
                                <DropdownMenuItem
                                    key={l.to}
                                    asChild
                                    className="p-0 focus:bg-transparent"
                                >
                                    <a
                                        href={l.to}
                                        onClick={onCalc}
                                        data-testid="nav-products-calculator"
                                        className={ItemLinkCls(false)}
                                    >
                                        {l.label}
                                    </a>
                                </DropdownMenuItem>
                            ) : (
                                <DropdownMenuItem
                                    key={l.to}
                                    asChild
                                    className="p-0 focus:bg-transparent"
                                >
                                    <Link
                                        to={l.to}
                                        data-testid={`nav-products-${l.to.replace('/', '')}`}
                                        className={ItemLinkCls(pathname === l.to)}
                                    >
                                        {l.label}
                                    </Link>
                                </DropdownMenuItem>
                            )
                        )}
                    </ul>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const StandardMenu = ({ id, label, links, pathname }) => {
    const active = isPathActive(
        pathname,
        links.map((l) => l.to)
    );
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                data-testid={`nav-${id}-trigger`}
                aria-haspopup="menu"
                className={TriggerCls(active)}
            >
                {label}
                <ChevronDown
                    size={14}
                    className="transition-transform duration-150 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                sideOffset={14}
                data-testid={`nav-${id}-content`}
                className="min-w-[240px] border border-brand-hairline bg-brand-surface p-1 text-brand-text shadow-[0_14px_36px_-14px_rgba(0,0,0,0.7)]"
            >
                {links.map((l) => (
                    <DropdownMenuItem
                        key={l.to}
                        asChild
                        className="p-0 focus:bg-transparent"
                    >
                        <Link
                            to={l.to}
                            data-testid={`nav-${id}-${l.to.replace(/\//g, '-')}`}
                            className={ItemLinkCls(pathname === l.to)}
                        >
                            {l.label}
                        </Link>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const NavLinkPlain = ({ to, label, testId }) => (
    <NavLink
        to={to}
        data-testid={testId}
        className={({ isActive }) =>
            [
                'relative whitespace-nowrap font-sans text-sm font-medium uppercase tracking-[0.12em] transition-colors duration-150',
                isActive ? 'text-brand-gold' : 'text-brand-muted hover:text-brand-text',
            ].join(' ')
        }
    >
        {({ isActive }) => (
            <>
                {label}
                <span
                    className={[
                        'absolute -bottom-2 left-0 h-px bg-brand-gold transition-all duration-200',
                        isActive ? 'w-full' : 'w-0',
                    ].join(' ')}
                />
            </>
        )}
    </NavLink>
);

const PhoneLink = () => (
    <a
        href={`tel:${SITE.phone}`}
        data-testid="header-phone"
        className="inline-flex items-center gap-2 whitespace-nowrap font-sans text-sm font-semibold tabular-nums text-brand-gold transition-colors duration-150 hover:text-brand-gold-hover"
    >
        <Phone size={14} />
        {SITE.phoneDisplay}
    </a>
);

/* ------------------------------------------------------------------ *
 * Mobile drawer - accordion with sticky phone + CTA at the top
 * ------------------------------------------------------------------ */

const MobileSection = ({ id, label, links, onCalc, onClose }) => (
    <AccordionItem
        value={id}
        className="border-b border-brand-hairline"
        data-testid={`mobile-acc-${id}`}
    >
        <AccordionTrigger
            data-testid={`mobile-acc-trigger-${id}`}
            className="px-1 py-4 font-sans text-base uppercase tracking-[0.12em] text-brand-text hover:no-underline data-[state=open]:text-brand-gold"
        >
            {label}
        </AccordionTrigger>
        <AccordionContent className="pb-2">
            <ul className="flex flex-col">
                {links.map((l) =>
                    l.isCalc ? (
                        <li key={l.to}>
                            <a
                                href={l.to}
                                onClick={(e) => {
                                    onCalc(e);
                                    onClose();
                                }}
                                data-testid={`mobile-${id}-calculator`}
                                className="block border-l-2 border-brand-blue-soft py-3 pl-5 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-text hover:text-brand-gold"
                            >
                                {l.label}
                            </a>
                        </li>
                    ) : (
                        <li key={l.to}>
                            <Link
                                to={l.to}
                                onClick={onClose}
                                data-testid={`mobile-${id}-${l.to.replace(/\//g, '-')}`}
                                className="block border-l-2 border-brand-blue-soft py-3 pl-5 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-text hover:text-brand-gold"
                            >
                                {l.label}
                            </Link>
                        </li>
                    )
                )}
            </ul>
        </AccordionContent>
    </AccordionItem>
);

/* ------------------------------------------------------------------ *
 * Header
 * ------------------------------------------------------------------ */

export const Header = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { user } = useAuth();
    const onCalcMobile = useScrollToCalculator();
    const closeMobile = useRef(() => setOpen(false));
    closeMobile.current = () => setOpen(false);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    return (
        <>
            <header
                className="sticky top-0 z-50 border-b border-brand-hairline bg-brand-base/90 backdrop-blur-md supports-[backdrop-filter]:bg-brand-base/75"
                data-testid="site-header"
            >
                <div className="mx-auto flex min-h-[96px] max-w-[1600px] items-center justify-between gap-6 px-5 py-4 lg:px-8 lg:py-5 xl:px-10">
                    <div className="lg:hidden flex-none">
                        <Brand height={44} eager />
                    </div>
                    <div className="hidden lg:block flex-none">
                        <Brand height={56} eager />
                    </div>

                    <nav
                        className="hidden lg:flex items-center gap-5 xl:gap-7"
                        aria-label="Primary"
                        data-testid="nav-desktop"
                    >
                        <NavLinkPlain to="/" label="Home" testId="nav-home" />
                        <ProductsMenu pathname={location.pathname} />
                        <StandardMenu
                            id="projects"
                            label="Projects"
                            links={PROJECTS_LINKS}
                            pathname={location.pathname}
                        />
                        <StandardMenu
                            id="partnerships"
                            label="Partnerships"
                            links={PARTNERSHIPS_LINKS}
                            pathname={location.pathname}
                        />
                        <NavLinkPlain to="/about" label="About Us" testId="nav-about" />
                        <NavLinkPlain to="/contact" label="Contact" testId="nav-contact" />
                    </nav>

                    <div className="hidden lg:flex items-center gap-4 xl:gap-6">
                        <PhoneLink />
                        {user ? <UserChip /> : null}
                        <Link
                            to="/quote"
                            data-testid="cta-quote"
                            className="whitespace-nowrap bg-brand-gold px-4 py-2 font-sans text-xs font-semibold uppercase tracking-[0.14em] text-brand-base transition-colors duration-150 hover:bg-brand-gold-hover"
                        >
                            Get a Quote
                        </Link>
                    </div>

                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex h-10 w-10 items-center justify-center border border-brand-hairline text-brand-text"
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        aria-expanded={open}
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </header>

            {open && (
                <div
                    className="lg:hidden fixed inset-x-0 top-24 bottom-0 z-[60] flex flex-col bg-brand-base border-t border-brand-hairline"
                    data-testid="mobile-drawer"
                >
                    {/* STICKY top - phone + CTA always visible */}
                    <div
                        className="sticky top-0 z-10 flex flex-col gap-3 border-b border-brand-hairline bg-brand-base px-6 py-4"
                        data-testid="mobile-sticky-top"
                    >
                        <a
                            href={`tel:${SITE.phone}`}
                            data-testid="mobile-phone"
                            className="flex items-center gap-3 font-sans text-base tabular-nums text-brand-gold transition-colors hover:text-brand-gold-hover"
                            onClick={closeMobile.current}
                        >
                            <Phone size={16} />
                            {SITE.phoneDisplay}
                        </a>
                        <Link
                            to="/quote"
                            data-testid="mobile-cta-quote"
                            onClick={closeMobile.current}
                            className="bg-brand-gold px-4 py-3 text-center font-sans text-xs font-semibold uppercase tracking-[0.14em] text-brand-base"
                        >
                            Get a Quote
                        </Link>
                    </div>

                    {/* Scrollable accordion */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        <NavLink
                            to="/"
                            end
                            onClick={closeMobile.current}
                            data-testid="mobile-nav-home"
                            className={({ isActive }) =>
                                [
                                    'block border-b border-brand-hairline py-4 font-sans text-base uppercase tracking-[0.12em] transition-colors',
                                    isActive ? 'text-brand-gold' : 'text-brand-text',
                                ].join(' ')
                            }
                        >
                            Home
                        </NavLink>
                        <Accordion type="multiple" className="w-full">
                            <MobileSection
                                id="products"
                                label="Products"
                                links={[...PRODUCTS_LINKS, ...PRODUCTS_SECONDARY]}
                                onCalc={onCalcMobile}
                                onClose={closeMobile.current}
                            />
                            <MobileSection
                                id="projects"
                                label="Projects"
                                links={PROJECTS_LINKS}
                                onCalc={onCalcMobile}
                                onClose={closeMobile.current}
                            />
                            <MobileSection
                                id="partnerships"
                                label="Partnerships"
                                links={PARTNERSHIPS_LINKS}
                                onCalc={onCalcMobile}
                                onClose={closeMobile.current}
                            />
                        </Accordion>

                        <NavLink
                            to="/about"
                            onClick={closeMobile.current}
                            data-testid="mobile-nav-about"
                            className={({ isActive }) =>
                                [
                                    'block border-b border-brand-hairline py-4 font-sans text-base uppercase tracking-[0.12em] transition-colors',
                                    isActive ? 'text-brand-gold' : 'text-brand-text',
                                ].join(' ')
                            }
                        >
                            About Us
                        </NavLink>
                        <NavLink
                            to="/contact"
                            onClick={closeMobile.current}
                            data-testid="mobile-nav-contact"
                            className={({ isActive }) =>
                                [
                                    'block border-b border-brand-hairline py-4 font-sans text-base uppercase tracking-[0.12em] transition-colors',
                                    isActive ? 'text-brand-gold' : 'text-brand-text',
                                ].join(' ')
                            }
                        >
                            Contact
                        </NavLink>

                        {user ? (
                            <Link
                                to="/admin"
                                onClick={closeMobile.current}
                                data-testid="mobile-cta-portal"
                                className="mt-6 block border border-brand-gold bg-brand-surface px-4 py-3 text-center font-sans text-xs font-semibold uppercase tracking-[0.14em] text-brand-gold"
                            >
                                {user.role === 'admin' ? 'Admin Portal' : 'Account'}
                            </Link>
                        ) : null}
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;
